import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { Session } from "@models/session";
import { SuccessResponse } from "@models/success-response";
import { PageResponse } from "@models/page-response";
import { PageOptions } from "@models/page-options";
import { SessionStartTime } from "@models/session-start-time";
import { SentimentSummaryResponse } from "@models/sentiment";
import { planApi } from "@store/planApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export interface GetSessionsQuery extends PageOptions {
  patientId: string;
}

export const sessionApi = createApi({
  reducerPath: "sessionApi",
  tagTypes: ["Session"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/sessions`,
    prepareHeaders: prepareAuthHeaders,
  }),
  endpoints: (builder) => ({
    createSession: builder.mutation<Session, Partial<Session>>({
      invalidatesTags: ["Session"],
      query: (session: Partial<Session>) => {
        return {
          url: "",
          method: "POST",
          body: session,
        };
      },
    }),
    closeSession: builder.mutation<SuccessResponse, string>({
      invalidatesTags: ["Session"],
      query: (id: string) => {
        return {
          url: `${id}/close`,
          method: "PATCH",
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(planApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Close is best-effort; network/tab suspend must not surface as an unhandled rejection.
        }
      },
    }),
    startSession: builder.mutation<SessionStartTime, string>({
      query: (id: string) => {
        return {
          url: `${id}/start`,
          method: "PATCH",
        };
      },
    }),
    getSessions: builder.query<PageResponse<Session>, GetSessionsQuery>({
      providesTags: ["Session"],
      query: (params) => {
        return {
          url: "",
          method: "GET",
          params,
        };
      },
    }),
    getSentimentSummary: builder.query<
      SentimentSummaryResponse,
      { patientId: string }
    >({
      providesTags: ["Session"],
      query: ({ patientId }) => ({
        url: "sentiment/summary",
        method: "GET",
        params: { patientId },
      }),
    }),
  }),
});

export const {
  useCreateSessionMutation,
  useCloseSessionMutation,
  useStartSessionMutation,
  useGetSessionsQuery,
  useGetSentimentSummaryQuery,
} = sessionApi;
